// Implements numerous CSV export APIs.

import { BidderTableData } from "@/app/(bidders)/columns";
import { formatAddress } from "@/lib/utils";
import { User } from "@prisma/client";

// Importing now exists in the parseCSV lambda.
export class CSVService {
  readonly CSVHeaders: string[] = [
    "Lot #",
    "Title",
    "Description",
    "Starting price",
    "Appraisal Min",
    "Appraisal Max",
    "Product Dimensions",
    "Category",
    "CoCode",
    "Inventory #",
    "Winner's instructions",
    "Disabled",
  ];

  readonly BidCSVHeaders: string[] = [
    "Lot #",
    "Title",
    "Description",
    "CoCode",
    "Inventory #",
    "Bidder",
    "Amount",
    "Date",
    "Time",
    "Email",
    "Is Winning Bid",
  ];

  readonly ConsignorCSVHeaders: string[] = [
    "CoCode",
    "First Name",
    "Last Name",
    "email",
  ];

  readonly InvoiceManagementHeaders: string[] = [
    "Invoice Id",
    "Name",
    "Email",
    "Lot Count",
    "Invoice Amount",
    "Payment Method",
    "Due Date",
    "Status",
  ];

  readonly BidderHeaders: string[] = [
    "Name",
    "Email",
    "Phone",
    "Address",
    "Group",
    "Tax Region",
    "Created At",
  ];


  // Method to format Lots to CSV data
  // (for exporting)
  lotsToCSV(lots: any[]) {
    // Convert the object to an array of its values
    const dataArray = Object.values(lots);

    // Map each object to the new format
    try {
      return dataArray.map((item) => {
        return {
          "Lot #": item.catalogNumber,
          Title: this._escape(item.title),
          Description: this._escape(item.description),
          "Starting price": item.startBid,
          "Appraisal Min": item.appraisalMin,
          "Appraisal Max": item.appraisalMax,
          "Additional Information": this._escape(item.additionalInfo),
          Category: item.category,
          CoCode: item.consignorId,
          "Inventory #": item.inventoryNumber,
          "Winner's instructions": this._escape(item.winnerInstructions),
          Disabled: item.isDisabled,
        };
      });
    } catch (error) {
      if (error instanceof Error)
        console.error("Couldn't map dataArray, ", error.message);
    }
  }

  // Method to format Lots to CSV data
  // (for exporting)
  bidsToCSV(bids: any[]) {
    const dataArray = Object.values(bids);
    return dataArray.map((item) => ({
      "Lot #": item.catalogNumber,
      Title: this._escape(item.title),
      Description: this._escape(item.description),
      CoCode: item.consignorId,
      "Inventory #": item.inventoryNumber,
      Bidder: `${item.userName}${item.lastName ? " " + item.lastName : ""}`,
      Amount: item.amount,
      Date: item.createdAtDate,
      Time: item.createdAtTime,
      Email: item.email,
      "Is Winning Bid": item.isWinningBid,
    }));
  }

  consignorsToCSV(consignors: any[]) {
    const dataArray = Object.values(consignors);
    return dataArray.map((item) => ({
      CoCode: item.cocode,
      "First Name": item.name,
      "Last Name": item.lastName,
      email: item.email,
    }));
  }

  invoicesToCSV(invoices: any[]) {
    const dataArray = Object.values(invoices);
    return dataArray.map((item) => ({
      "Invoice Id": item.id,
      Name: item?.user?.name + " " + item?.user?.lastName,
      Email: item?.user?.email,
      "Lot Count": item.numberOfLots,
      "Invoice Amount": item.totalAmount,
      "Payment Method": item.paymentMethod,
      "Due Date": item.dueAt,
      Status: item.invoiceStatus,
    }));
  }

  biddersToCSV(bidders: any[]) {
    const dataArray = Object.values(bidders);
    return dataArray.map((item) => {
      const firstAddress = item?.Address?.[0] ?? null;
      const formattedAddress = formatAddress(firstAddress);
      return {
        Name: item?.name + " " + item?.lastName,
        Email: item?.email,
        Phone: item?.phoneNumber,
        Address: formattedAddress,
        Group: item?.bidderGroup?.name,
        "Tax Region": item?.taxRegion,
        "Created At": item.createdAt,
      }
    });
  }

  _escape(value: any) {
    if (typeof value !== "string") {
      return value;
    }

    // Escape double quotes and wrap the string in double quotes
    return `${value.replace(/"/g, '""')}`;
  }
}
